import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Camera, DollarSign, Clock, TrendingUp, CheckCircle2, ArrowRight, ClipboardCheck, ChevronLeft, Package, Shirt } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGIONS = [
  "Southside Chicago",
  "Westside Chicago",
  "Northside Chicago",
  "South Suburbs",
  "North Suburbs",
  "West Suburbs",
  "Southwest Suburbs",
  "Northwest Indiana",
];
// Added 15-18 range so high-school applicants can apply (parent/guardian
// sign-off will still be required at onboarding — flag captured for review).
const AGE_RANGES = ["15–18", "18–24", "25–34", "35–44", "45–54", "55+"];

const perks = [
  { icon: DollarSign, title: "Same-day pay", desc: "Get paid directly after every game — no waiting on payroll cycles." },
  { icon: Clock, title: "Lunch stipend", desc: "Every filming day includes a stipend for lunch on the sideline." },
  { icon: TrendingUp, title: "Bonus incentives", desc: "Performance bonuses for top-quality footage and fast turnaround." },
  { icon: Camera, title: "Film & upload games", desc: "Capture youth sports on YSV-provided gear, upload straight from the field." },
];

/* ─── Pre-screening questionnaire schema ─────────────────────────────────
   Digitized verbatim from the "YSV Game Day Operator Pre-Screening
   Questionnaire" PDF. Each item drives a question control in the form.
   `type`: "yesno" | "yesno_na" | "choice" | "text" | "textarea" | "ack"
──────────────────────────────────────────────────────────────────────── */
const PRE_SCREENING_SECTIONS = [
  {
    key: "availability",
    title: "Availability",
    questions: [
      { id: "saturdays_sundays", label: "Are you available to work Saturdays and/or Sundays during football season (August through November)?", type: "yesno" },
      { id: "weekends_per_month", label: "How many weekends per month are you available?", type: "choice", options: ["1–2", "3–4", "Every weekend"] },
      { id: "holidays", label: "Are you available to work holidays if games are scheduled?", type: "yesno" },
    ],
  },
  {
    key: "transportation",
    title: "Transportation",
    questions: [
      { id: "reliable_transport", label: "Do you have reliable transportation?", type: "yesno" },
      { id: "rideshare_ok", label: "If you do not have reliable transportation, are you comfortable using Uber or another rideshare service to travel to games?", type: "yesno_na" },
      { id: "travel_distance", label: "Approximately how far are you willing to travel for a game? (miles or minutes)", type: "text", placeholder: "e.g. 30 miles / 45 minutes" },
    ],
  },
  {
    key: "physical",
    title: "Physical Requirements",
    questions: [
      { id: "lift_30_40", label: "Are you able to lift and carry 30–40 pounds?", type: "yesno" },
      {
        id: "backpack_contents",
        type: "note",
        title: "The YSV equipment backpack contains:",
        items: [
          "Camera — 4 lbs",
          "Phone / USB power banks — 1 lb",
          "Tripod — 7 lbs",
          "Chair — 6 lbs",
          "Weights for tripod — 15 lbs",
        ],
        footer: "Total ≈ 33 lbs",
      },
      { id: "outdoor_3_7hr", label: "Are you comfortable sitting, standing and walking outdoors for 3–7 hours in various weather conditions?", type: "yesno" },
      { id: "weather_ok", label: "Are you comfortable working in temperatures ranging from hot summer days to cold fall mornings?", type: "yesno" },
      {
        id: "apparel_provided",
        type: "note",
        title: "We will provide apparel to keep you comfortable in the elements, including:",
        items: [
          "Coat", "Jacket", "Hoodie", "Hat", "Scarf", "Gloves", "Hand / body warmers",
        ],
        layout: "chips",
      },
    ],
  },
  {
    key: "technology",
    title: "Technology Experience",
    questions: [
      { id: "tech_comfortable", label: "Are you comfortable using smartphones, tablets, computers, and camera equipment?", type: "yesno" },
      { id: "camera_experience", label: "Have you ever operated a camera or recorded sporting events?", type: "yesno" },
      { id: "camera_experience_details", label: "If yes, please describe your experience:", type: "textarea", showIf: { camera_experience: "yes" } },
    ],
  },
  {
    key: "customer_service",
    title: "Customer Service",
    questions: [
      { id: "customer_service_ok", label: "Are you comfortable interacting professionally with coaches, parents, athletes, and event staff?", type: "yesno" },
      { id: "troubleshoot_ok", label: "If technical issues arise during a game, are you comfortable troubleshooting with assistance from our support team?", type: "yesno" },
    ],
  },
  {
    key: "employment",
    title: "Employment",
    questions: [
      { id: "legally_authorized", label: "Are you legally authorized to work in the United States?", type: "yesno" },
      { id: "felony_conviction", label: "Have you ever been convicted of a felony that would prevent you from working around youth sporting events? (Answering \"Yes\" does not automatically disqualify you.)", type: "yesno" },
    ],
  },
  {
    key: "why_ysv",
    title: "Why YSV?",
    questions: [
      { id: "why_ysv", label: "Why are you interested in becoming a YSV Game Day Operator?", type: "textarea" },
    ],
  },
  {
    key: "acknowledgement",
    title: "Final Acknowledgement",
    questions: [
      { id: "ack_weekend", label: "I understand this position requires weekend availability during football season.", type: "ack" },
      { id: "ack_equipment", label: "I understand I will be responsible for safely transporting and operating YSV camera equipment.", type: "ack" },
      { id: "ack_professionalism", label: "I understand punctuality and professionalism are expected at every event.", type: "ack" },
      { id: "ack_outdoors", label: "I understand that Game Day Operators may work outdoors for extended periods.", type: "ack" },
      { id: "ack_certify", label: "I certify that the information provided above is accurate to the best of my knowledge.", type: "ack" },
    ],
  },
];

// Flatten all question ids so we can validate "all required questions answered".
// Excludes non-answerable items: free-text follow-ups (textarea) and the two
// informational note callouts (equipment backpack list + apparel list).
const ALL_REQUIRED_PS_IDS = PRE_SCREENING_SECTIONS.flatMap(s =>
  s.questions
    .filter(q => q.type !== "textarea" && q.type !== "note" && q.id !== "camera_experience_details")
    .map(q => q.id)
);

const CareersPage = () => {
  // Two-step wizard: "intro" → "prescreening" → submit
  const [step, setStep] = useState("intro"); // "intro" | "prescreening"
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", regions: [], age_range: "",
    // Intro sports questions
    likes_sports: "",                 // "yes" | "no"
    interested_in_videography: "",    // only if likes_sports === "no"
    favorite_team_or_player: "",
  });
  const [preScreening, setPreScreening] = useState({}); // { question_id: answer }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const toggleRegion = (r) => {
    setForm(f => ({
      ...f,
      regions: f.regions.includes(r) ? f.regions.filter(x => x !== r) : [...f.regions, r],
    }));
  };
  const setPS = (id, val) => setPreScreening(p => ({ ...p, [id]: val }));

  // ── Step 1 validation → unlocks the pre-screening step ──
  const validateIntro = () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return "Please fill in your first and last name.";
    if (!form.phone.trim()) return "Please enter your phone number.";
    if (!form.email.trim() || !form.email.includes("@")) return "Please enter a valid email.";
    if (form.regions.length === 0) return "Pick at least one region you can work in.";
    if (!form.age_range) return "Please select your age range.";
    if (!form.likes_sports) return "Please tell us whether you like or have played a sport.";
    if (form.likes_sports === "no" && !form.interested_in_videography) return "Please tell us if you're interested in videography or filming.";
    if (!form.favorite_team_or_player.trim()) return "Please share your favorite sports team or player.";
    return null;
  };

  const validatePreScreening = () => {
    for (const id of ALL_REQUIRED_PS_IDS) {
      if (preScreening[id] === undefined || preScreening[id] === "" || preScreening[id] === false) {
        return "Please answer every question in the pre-screening section before submitting.";
      }
    }
    return null;
  };

  const goToPreScreening = (e) => {
    e.preventDefault();
    const err = validateIntro();
    if (err) { setError(err); return; }
    setError(null);
    setStep("prescreening");
    // Scroll to top after switching steps so the applicant sees the new section.
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 20);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const introErr = validateIntro();
    if (introErr) { setError(introErr); setStep("intro"); return; }
    const psErr = validatePreScreening();
    if (psErr) { setError(psErr); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/gdo-applications`, {
        ...form,
        favorite_team_or_player: form.favorite_team_or_player.trim(),
        pre_screening: preScreening,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Mailto fallback / copy to GDO@ysvchi.com — so the applicant can also send
  // themselves if a real email provider isn't yet wired.
  const mailtoHref = (() => {
    const body = `Name: ${form.first_name} ${form.last_name}\nPhone: ${form.phone}\nEmail: ${form.email}\nRegions: ${form.regions.join(", ")}\nAge range: ${form.age_range}`;
    return `mailto:GDO@ysvchi.com?subject=GDO%20Application%20-%20${encodeURIComponent(form.first_name + " " + form.last_name)}&body=${encodeURIComponent(body)}`;
  })();

  return (
    <div data-testid="careers-page" style={{ padding: '72px 24px 60px', minHeight: '100vh', background: 'var(--dark-bg)' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 44 }}>
          <p style={{ fontSize: 12, color: '#ff6b00', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>Careers · GDO</p>
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 'clamp(30px, 5vw, 48px)', fontWeight: 800, color: '#fff', marginTop: 8 }}>
            Become a Gameday Operator
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 16, marginTop: 12, maxWidth: 640, margin: '12px auto 0', lineHeight: 1.6 }}>
            Film and upload youth sports games, help us build the next generation of athletes' highlight reels,
            and contribute to local sports marketing — all on your own schedule.
          </p>
        </div>

        {/* Perks */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 48 }}>
          {perks.map(p => (
            <div key={p.title} className="glass-card" style={{ padding: 20 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(0,229,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <p.icon size={18} style={{ color: 'var(--cyan)' }} />
              </div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{p.title}</h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{p.desc}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        {submitted ? (
          <div data-testid="careers-success" className="glass-card" style={{ padding: 40, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
            <CheckCircle2 size={56} style={{ color: '#4caf50', marginBottom: 16 }} />
            <h2 style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Outfit', sans-serif", marginBottom: 10 }}>Application received!</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
              Thanks, {form.first_name}. Our team will review your info and reach out within 48 hours.
              We'll contact you at <strong style={{ color: '#fff' }}>{form.email}</strong> or <strong style={{ color: '#fff' }}>{form.phone}</strong>.
            </p>
            <Link to="/landing" className="btn-primary" style={{ textDecoration: 'none', padding: '12px 28px' }}>
              Back to YSV <ArrowRight size={14} style={{ verticalAlign: -2, marginLeft: 6 }} />
            </Link>
          </div>
        ) : step === "intro" ? (
          <IntroForm
            form={form}
            setForm={setForm}
            toggleRegion={toggleRegion}
            onNext={goToPreScreening}
            error={error}
            mailtoHref={mailtoHref}
          />
        ) : (
          <PreScreeningForm
            preScreening={preScreening}
            setPS={setPS}
            onBack={() => { setStep("intro"); setError(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            onSubmit={submit}
            submitting={submitting}
            error={error}
            applicantName={form.first_name}
          />
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Step 1: Basic info + intro sports questions.
   Ends with a "Continue" button that unlocks the pre-screening step.
────────────────────────────────────────────────────────────── */
const IntroForm = ({ form, setForm, toggleRegion, onNext, error, mailtoHref }) => (
  <form data-testid="careers-form" onSubmit={onNext} className="glass-card" style={{ padding: 32, maxWidth: 720, margin: '0 auto' }}>
    <h2 style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Outfit', sans-serif", marginBottom: 6, color: '#fff' }}>Apply now</h2>
    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
      Step 1 of 2 — Basic info. Application goes directly to <a href="mailto:GDO@ysvchi.com" style={{ color: 'var(--cyan)' }}>GDO@ysvchi.com</a>.
    </p>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>First name*
        <input data-testid="careers-first-name" required className="input-field" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
      </label>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Last name*
        <input data-testid="careers-last-name" required className="input-field" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
      </label>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Phone number*
        <input data-testid="careers-phone" required className="input-field" type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
      </label>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Email*
        <input data-testid="careers-email" required className="input-field" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
      </label>
    </div>

    <div style={{ marginTop: 18 }}>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>Regions you can work in* (select all that apply)</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 6 }}>
        {REGIONS.map(r => {
          const on = form.regions.includes(r);
          return (
            <label
              key={r}
              data-testid={`careers-region-${r.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={(e) => { e.preventDefault(); toggleRegion(r); }}
              style={{
                padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                background: on ? 'rgba(0,229,255,0.08)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${on ? 'var(--cyan)' : 'var(--dark-border)'}`,
                color: on ? 'var(--cyan)' : 'var(--text-primary)',
                fontSize: 13, fontWeight: on ? 600 : 500,
                display: 'flex', alignItems: 'center', gap: 8,
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{
                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                background: on ? 'var(--cyan)' : 'transparent',
                border: `1.5px solid ${on ? 'var(--cyan)' : 'var(--text-secondary)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{on && <CheckCircle2 size={10} style={{ color: '#000' }} />}</span>
              {r}
            </label>
          );
        })}
      </div>
    </div>

    <div style={{ marginTop: 18 }}>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>Age range*</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {AGE_RANGES.map(a => {
          const on = form.age_range === a;
          return (
            <button
              key={a}
              type="button"
              data-testid={`careers-age-${a.replace(/[^\w]/g, '')}`}
              onClick={() => setForm(f => ({ ...f, age_range: a }))}
              style={{
                padding: '8px 16px', borderRadius: 20, cursor: 'pointer',
                background: on ? 'rgba(0,229,255,0.12)' : 'transparent',
                border: `1px solid ${on ? 'var(--cyan)' : 'var(--dark-border)'}`,
                color: on ? 'var(--cyan)' : 'var(--text-primary)',
                fontSize: 13, fontWeight: on ? 700 : 500,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >{a}</button>
          );
        })}
      </div>
    </div>

    {/* Sports interest questions */}
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--dark-border)' }}>
      <QuestionYesNo
        testid="careers-likes-sports"
        label="Do you like sports or have you played a sport?"
        value={form.likes_sports}
        onChange={v => setForm(f => ({ ...f, likes_sports: v, interested_in_videography: v === "yes" ? "" : f.interested_in_videography }))}
      />
      {form.likes_sports === "no" && (
        <div style={{ marginTop: 14, padding: '14px 16px', borderRadius: 10, background: 'rgba(255,107,0,0.05)', border: '1px solid rgba(255,107,0,0.25)' }}>
          <QuestionYesNo
            testid="careers-videography"
            label="No problem — are you interested in videography or filming?"
            value={form.interested_in_videography}
            onChange={v => setForm(f => ({ ...f, interested_in_videography: v }))}
            accent="#ff6b00"
          />
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <label style={{ fontSize: 13, color: '#fff', fontWeight: 600, display: 'block', marginBottom: 8 }}>Who is your favorite sports team or player?*</label>
        <input
          data-testid="careers-favorite-team"
          className="input-field"
          placeholder="e.g. Chicago Bears / Justin Fields"
          value={form.favorite_team_or_player}
          onChange={e => setForm({ ...form, favorite_team_or_player: e.target.value })}
        />
      </div>
    </div>

    {error && <div data-testid="careers-error" style={{ marginTop: 16, padding: 10, background: 'rgba(244,67,54,0.12)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 10, color: '#f44336', fontSize: 13 }}>{error}</div>}

    {/* Opens step 2 (pre-screening questions); testid: careers-open-prescreening. */}
    <button
      data-testid="careers-open-prescreening"
      type="submit"
      className="btn-primary"
      style={{ width: '100%', marginTop: 20, padding: '14px 20px', fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
    >
      <ClipboardCheck size={16} />
      Continue to pre-screening questions
    </button>
    <p style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 10 }}>
      Step 2 of 2 · Takes about 3 minutes.
    </p>

    <p style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 12 }}>
      Prefer email? <a href={mailtoHref} style={{ color: 'var(--cyan)' }}>Send to GDO@ysvchi.com directly</a>
    </p>
  </form>
);

/* ─────────────────────────────────────────────────────────────
   Step 2: Full pre-screening questionnaire (16 questions in 8 sections).
   Final submit here performs the actual application POST.
────────────────────────────────────────────────────────────── */
const PreScreeningForm = ({ preScreening, setPS, onBack, onSubmit, submitting, error, applicantName }) => (
  <form data-testid="careers-prescreening" onSubmit={onSubmit} className="glass-card" style={{ padding: 32, maxWidth: 720, margin: '0 auto' }}>
    <button
      type="button"
      onClick={onBack}
      data-testid="prescreening-back"
      style={{
        background: 'none', border: 'none', color: 'var(--text-secondary)',
        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 13, marginBottom: 12,
      }}
    ><ChevronLeft size={14} /> Back to basics</button>

    <h2 style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Outfit', sans-serif", marginBottom: 6, color: '#fff' }}>
      Pre-screening questions
    </h2>
    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
      {applicantName ? `Almost done, ${applicantName}. ` : "Almost done. "}
      Please answer each question honestly.
    </p>

    {PRE_SCREENING_SECTIONS.map(section => (
      <div key={section.key} data-testid={`ps-section-${section.key}`} style={{ marginTop: 26 }}>
        <h3 style={{
          fontSize: 11, color: 'var(--cyan)', fontWeight: 700,
          letterSpacing: 2, textTransform: 'uppercase',
          borderBottom: '1px solid var(--dark-border)', paddingBottom: 8,
          marginBottom: 14,
        }}>{section.title}</h3>
        {section.questions.map(q => {
          // showIf gating (used for the "describe your experience" follow-up)
          if (q.showIf) {
            const [depKey, depVal] = Object.entries(q.showIf)[0];
            if (preScreening[depKey] !== depVal) return null;
          }
          return (
            <div key={q.id} style={{ marginBottom: 16 }}>
              {q.type === "note" && (
                <InfoNote note={q} />
              )}
              {q.type === "yesno" && (
                <QuestionYesNo
                  testid={`ps-${q.id}`}
                  label={q.label}
                  value={preScreening[q.id] || ""}
                  onChange={v => setPS(q.id, v)}
                />
              )}
              {q.type === "yesno_na" && (
                <QuestionChoice
                  testid={`ps-${q.id}`}
                  label={q.label}
                  value={preScreening[q.id] || ""}
                  onChange={v => setPS(q.id, v)}
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                    { value: "na", label: "N/A" },
                  ]}
                />
              )}
              {q.type === "choice" && (
                <QuestionChoice
                  testid={`ps-${q.id}`}
                  label={q.label}
                  value={preScreening[q.id] || ""}
                  onChange={v => setPS(q.id, v)}
                  options={q.options.map(o => ({ value: o, label: o }))}
                />
              )}
              {q.type === "text" && (
                <div>
                  <label style={{ fontSize: 13, color: '#fff', fontWeight: 600, display: 'block', marginBottom: 8 }}>{q.label}</label>
                  <input
                    data-testid={`ps-${q.id}`}
                    className="input-field"
                    placeholder={q.placeholder || ""}
                    value={preScreening[q.id] || ""}
                    onChange={e => setPS(q.id, e.target.value)}
                  />
                </div>
              )}
              {q.type === "textarea" && (
                <div>
                  <label style={{ fontSize: 13, color: '#fff', fontWeight: 600, display: 'block', marginBottom: 8 }}>{q.label}</label>
                  <textarea
                    data-testid={`ps-${q.id}`}
                    className="input-field"
                    rows={3}
                    value={preScreening[q.id] || ""}
                    onChange={e => setPS(q.id, e.target.value)}
                    style={{ resize: 'vertical', minHeight: 72, fontFamily: "'DM Sans', sans-serif" }}
                  />
                </div>
              )}
              {q.type === "ack" && (
                <label
                  data-testid={`ps-${q.id}`}
                  onClick={(e) => { e.preventDefault(); setPS(q.id, !preScreening[q.id]); }}
                  style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
                    padding: '10px 14px', borderRadius: 10,
                    background: preScreening[q.id] ? 'rgba(0,229,255,0.08)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${preScreening[q.id] ? 'var(--cyan)' : 'var(--dark-border)'}`,
                  }}
                >
                  <span style={{
                    width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                    background: preScreening[q.id] ? 'var(--cyan)' : 'transparent',
                    border: `1.5px solid ${preScreening[q.id] ? 'var(--cyan)' : 'var(--text-secondary)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{preScreening[q.id] && <CheckCircle2 size={11} style={{ color: '#000' }} />}</span>
                  <span style={{
                    fontSize: 13, color: preScreening[q.id] ? '#fff' : 'var(--text-primary)',
                    fontWeight: 500, lineHeight: 1.55,
                  }}>{q.label}</span>
                </label>
              )}
            </div>
          );
        })}
      </div>
    ))}

    {error && <div data-testid="prescreening-error" style={{ marginTop: 20, padding: 10, background: 'rgba(244,67,54,0.12)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 10, color: '#f44336', fontSize: 13 }}>{error}</div>}

    <button
      data-testid="prescreening-submit"
      type="submit"
      disabled={submitting}
      className="btn-primary"
      style={{ width: '100%', marginTop: 24, padding: '14px 20px', fontSize: 15, opacity: submitting ? 0.6 : 1 }}
    >{submitting ? "Submitting…" : "Submit application"}</button>
  </form>
);

/* ─── Shared question controls ─────────────────────────────────────── */

// Informational callout block (not a question — no answer captured). Used to
// preview equipment weight breakdown after the 30-40 lb question and to list
// the apparel YSV provides after the temperature-tolerance question.
const InfoNote = ({ note }) => {
  const Icon = note.id === "backpack_contents" ? Package : Shirt;
  return (
    <div
      data-testid={`ps-note-${note.id}`}
      style={{
        padding: '14px 16px', borderRadius: 12,
        background: 'rgba(0,229,255,0.04)',
        border: '1px dashed rgba(0,229,255,0.35)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Icon size={16} style={{ color: 'var(--cyan)' }} />
        <p style={{ fontSize: 13, color: '#fff', fontWeight: 700, margin: 0 }}>{note.title}</p>
      </div>
      {note.layout === "chips" ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {note.items.map((it, i) => (
            <span
              key={i}
              style={{
                padding: '4px 10px', borderRadius: 999, fontSize: 12,
                background: 'rgba(0,229,255,0.08)',
                border: '1px solid rgba(0,229,255,0.28)',
                color: '#d7f4fa', fontWeight: 600,
              }}
            >{it}</span>
          ))}
        </div>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.7 }}>
          {note.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      )}
      {note.footer && (
        <p style={{ marginTop: 8, fontSize: 12, color: 'var(--cyan)', fontWeight: 600 }}>{note.footer}</p>
      )}
    </div>
  );
};

const QuestionYesNo = ({ label, value, onChange, testid, accent = "var(--cyan)" }) => (
  <div>
    <label style={{ fontSize: 13, color: '#fff', fontWeight: 600, display: 'block', marginBottom: 8, lineHeight: 1.5 }}>{label}</label>
    <div style={{ display: 'flex', gap: 8 }}>
      {[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }].map(opt => {
        const on = value === opt.v;
        return (
          <button
            key={opt.v}
            type="button"
            data-testid={`${testid}-${opt.v}`}
            onClick={() => onChange(opt.v)}
            style={{
              flex: 1, padding: '10px 16px', borderRadius: 10, cursor: 'pointer',
              background: on ? (accent === "var(--cyan)" ? 'rgba(0,229,255,0.12)' : 'rgba(255,107,0,0.12)') : 'transparent',
              border: `1px solid ${on ? accent : 'var(--dark-border)'}`,
              color: on ? accent : 'var(--text-primary)',
              fontSize: 13, fontWeight: on ? 700 : 500,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >{opt.l}</button>
        );
      })}
    </div>
  </div>
);

const QuestionChoice = ({ label, value, onChange, options, testid }) => (
  <div>
    <label style={{ fontSize: 13, color: '#fff', fontWeight: 600, display: 'block', marginBottom: 8, lineHeight: 1.5 }}>{label}</label>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map(opt => {
        const on = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`${testid}-${opt.value}`}
            onClick={() => onChange(opt.value)}
            style={{
              padding: '9px 16px', borderRadius: 10, cursor: 'pointer',
              background: on ? 'rgba(0,229,255,0.12)' : 'transparent',
              border: `1px solid ${on ? 'var(--cyan)' : 'var(--dark-border)'}`,
              color: on ? 'var(--cyan)' : 'var(--text-primary)',
              fontSize: 13, fontWeight: on ? 700 : 500,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >{opt.label}</button>
        );
      })}
    </div>
  </div>
);

export default CareersPage;
